import React, { useState, useEffect } from "react";
import {
  Shield,
  Calendar,
  MapPin,
  User,
  Phone,
  FileText,
  CheckCircle,
  AlertTriangle,
  Clock,
  Filter,
  Search,
  Eye,
  Stethoscope,
  Building2,
  Plus,
  Trash2,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";

const VaccinationRecordsPage = ({ user }) => {
  const { themeClasses } = useTheme();
  const [pets, setPets] = useState([]);
  const [selectedPet, setSelectedPet] = useState(null);
  const [vaccinations, setVaccinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingVaccinations, setLoadingVaccinations] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVaccination, setSelectedVaccination] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Initialize Firebase Functions
  const functions = getFunctions(app);

  useEffect(() => {
    if (user) {
      loadUserPets();
    }
  }, [user]);

  useEffect(() => {
    if (selectedPet) {
      loadVaccinationRecords(selectedPet.id);
    }
  }, [selectedPet]);

  const loadUserPets = async () => {
    if (!user?.uid) {
      console.log("No user ID available");
      return;
    }

    try {
      setLoading(true);
      console.log("Loading pets for user:", user.uid);

      // Try Firebase function first
      try {
        const getUserPets = httpsCallable(functions, "getUserPets");
        const result = await getUserPets();

        if (result.data.success) {
          const petsData = result.data.pets;
          console.log("Firebase function - Setting pets:", petsData);
          setPets(petsData);

          if (petsData.length > 0 && !selectedPet) {
            setSelectedPet(petsData[0]);
          }
          return; // Success, exit early
        } else {
          console.error("Firebase function failed:", result.data.message);
        }
      } catch (functionError) {
        console.error("Firebase function error:", functionError);
        console.log("Falling back to direct Firestore query...");

        // Fallback to direct Firestore query for debugging
        const { collection, query, where, getDocs } = await import(
          "firebase/firestore"
        );

        const petsQuery = query(
          collection(db, "pets"),
          where("ownerId", "==", user.uid)
        );
        const petsSnapshot = await getDocs(petsQuery);
        console.log("Direct query returned", petsSnapshot.docs.length, "pets");

        const petsData = petsSnapshot.docs.map((doc) => {
          const data = doc.data();
          console.log("Pet data:", data);
          return {
            id: doc.id,
            ...data,
          };
        });

        // Sort pets by name
        petsData.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });

        console.log("Direct query - Setting pets:", petsData);
        setPets(petsData);

        if (petsData.length > 0 && !selectedPet) {
          setSelectedPet(petsData[0]);
        }
      }
    } catch (error) {
      console.error("Error loading pets:", error);
      setPets([]);
    } finally {
      setLoading(false);
    }
  };

  const loadVaccinationRecords = async (petId) => {
    try {
      setLoadingVaccinations(true);
      // Try Firebase function first
      try {
        const getVaccinationRecords = httpsCallable(
          functions,
          "getVaccinationRecords"
        );
        const result = await getVaccinationRecords({ petId });

        if (result.data.success) {
          console.log(
            "Firebase function - Setting vaccinations:",
            result.data.vaccinations
          );
          setVaccinations(result.data.vaccinations);
          return; // Success, exit early
        } else {
          console.error("Firebase function failed:", result.data.message);
        }
      } catch (functionError) {
        console.error("Firebase function error:", functionError);
        console.log(
          "Falling back to direct Firestore query for vaccinations..."
        );

        // Fallback to direct Firestore query for debugging
        const { collection, query, getDocs } = await import(
          "firebase/firestore"
        );

        const vaccinationsQuery = query(
          collection(db, "pets", petId, "vaccinations")
        );
        const vaccinationsSnapshot = await getDocs(vaccinationsQuery);
        const vaccinationsData = vaccinationsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort in JavaScript instead of Firestore
        vaccinationsData.sort((a, b) => {
          const dateA = new Date(
            a.dateAdministered?.seconds
              ? a.dateAdministered.seconds * 1000
              : a.dateAdministered
          );
          const dateB = new Date(
            b.dateAdministered?.seconds
              ? b.dateAdministered.seconds * 1000
              : b.dateAdministered
          );
          return dateB - dateA; // Descending order (newest first)
        });

        console.log("Direct query - Setting vaccinations:", vaccinationsData);
        setVaccinations(vaccinationsData);
      }
    } catch (error) {
      console.error("Error loading vaccination records:", error);
      setVaccinations([]);
    } finally {
      setLoadingVaccinations(false);
    }
  };

  const addTestVaccination = async () => {
    if (!selectedPet) return;

    try {
      const testVaccination = {
        vaccineName: "Rabies",
        manufacturer: "Nobivac",
        batchNumber: "RBX-2024-001",
        dateAdministered: new Date("2024-01-15T10:00:00"),
        validUntil: new Date("2025-01-15T23:59:59"),
        vetName: "Dr. Sarah Johnson",
        vetContact: "+47 123 456 78",
        vetLicense: "VET-12345",
        clinicAddress: "123 Main St, Oslo, Norway",
        notes: "Annual rabies vaccination - pet was healthy and cooperative",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(
        collection(db, "pets", selectedPet.id, "vaccinations"),
        testVaccination
      );

      // Reload vaccination records
      loadVaccinationRecords(selectedPet.id);

      alert("Test vaccination record added successfully!");
    } catch (error) {
      console.error("Error adding test vaccination:", error);
      alert("Failed to add test vaccination: " + error.message);
    }
  };

  const deleteVaccination = async (vaccinationId, vaccineName) => {
    if (!selectedPet) return;

    if (
      !window.confirm(
        `Are you sure you want to delete the vaccination record for "${vaccineName}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setDeleting(true);

      const deleteVaccinationFn = httpsCallable(functions, "deleteVaccination");
      const result = await deleteVaccinationFn({
        petId: selectedPet.id,
        vaccinationId,
        confirmDelete: true,
      });

      if (result.data.success) {
        // Reload vaccination records
        await loadVaccinationRecords(selectedPet.id);
        alert("Vaccination record deleted successfully!");
      } else {
        throw new Error(result.data.message || "Failed to delete vaccination");
      }
    } catch (error) {
      console.error("Error deleting vaccination:", error);
      alert("Failed to delete vaccination: " + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const getVaccinationStatus = (vaccination) => {
    const now = new Date();
    const validUntil = new Date(
      vaccination.validUntil?.seconds
        ? vaccination.validUntil.seconds * 1000
        : vaccination.validUntil
    );

    if (validUntil < now) {
      return { status: "expired", color: "red", icon: AlertTriangle };
    } else if (
      validUntil.getTime() - now.getTime() <
      30 * 24 * 60 * 60 * 1000
    ) {
      return { status: "expiring_soon", color: "yellow", icon: Clock };
    } else {
      return { status: "active", color: "green", icon: CheckCircle };
    }
  };

  const formatDate = (date) => {
    if (!date) return "Unknown";
    const dateObj = date?.seconds
      ? new Date(date.seconds * 1000)
      : new Date(date);
    return dateObj.toLocaleDateString();
  };

  const filteredVaccinations = vaccinations.filter((vaccination) => {
    const matchesSearch =
      vaccination.vaccineName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      vaccination.manufacturer
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      vaccination.vetName?.toLowerCase().includes(searchTerm.toLowerCase());

    if (filterStatus === "all") return matchesSearch;

    const { status } = getVaccinationStatus(vaccination);
    return matchesSearch && status === filterStatus;
  });

  const VaccinationCard = ({ vaccination }) => {
    const {
      status,
      color,
      icon: StatusIcon,
    } = getVaccinationStatus(vaccination);

    return (
      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-4 hover:border-cyan-500/50 transition-colors`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg bg-${color}-500/20`}>
              <Shield className={`w-5 h-5 text-${color}-400`} />
            </div>
            <div>
              <h3 className={`${themeClasses.text.primary} font-semibold`}>
                {vaccination.vaccineName}
              </h3>
              <p className={`${themeClasses.text.secondary} text-sm`}>
                {vaccination.manufacturer}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <StatusIcon className={`w-4 h-4 text-${color}-400`} />
            <button
              onClick={() => setSelectedVaccination(vaccination)}
              className={`p-1 ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
              title="View Details"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={() =>
                deleteVaccination(vaccination.id, vaccination.vaccineName)
              }
              disabled={deleting}
              className={`p-1 ${themeClasses.text.secondary} hover:text-red-400 transition-colors disabled:opacity-50`}
              title="Delete Vaccination"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <label className={`${themeClasses.text.tertiary} text-xs`}>
              Date Administered
            </label>
            <p
              className={`${themeClasses.text.primary} text-sm flex items-center`}
            >
              <Calendar className="w-3 h-3 mr-1" />
              {formatDate(vaccination.dateAdministered)}
            </p>
          </div>
          <div>
            <label className={`${themeClasses.text.tertiary} text-xs`}>
              Valid Until
            </label>
            <p
              className={`${themeClasses.text.primary} text-sm flex items-center`}
            >
              <Clock className="w-3 h-3 mr-1" />
              {formatDate(vaccination.validUntil)}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div
            className={`text-sm ${themeClasses.text.secondary} flex items-center`}
          >
            <Stethoscope className="w-3 h-3 mr-1" />
            {vaccination.vetName}
          </div>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium bg-${color}-500/20 text-${color}-400`}
          >
            {status.replace("_", " ").toUpperCase()}
          </span>
        </div>
      </div>
    );
  };

  const VaccinationDetailModal = ({ vaccination, onClose }) => {
    if (!vaccination) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div
          className={`${themeClasses.bg.modal} rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden border ${themeClasses.border.primary}`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between p-6 border-b ${themeClasses.border.primary}`}
          >
            <div>
              <h2
                className={`text-xl font-bold ${themeClasses.text.primary} flex items-center`}
              >
                <Shield className="w-5 h-5 mr-2 text-cyan-400" />
                {vaccination.vaccineName}
              </h2>
              <p className={`${themeClasses.text.secondary} text-sm mt-1`}>
                Vaccination Record Details
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 ${themeClasses.text.secondary} hover:${themeClasses.text.primary} hover:${themeClasses.bg.secondary} rounded-lg transition-colors`}
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Vaccination Details */}
              <div className="space-y-4">
                <h3
                  className={`text-lg font-semibold ${themeClasses.text.primary} mb-3`}
                >
                  Vaccination Information
                </h3>

                <div>
                  <label className={`${themeClasses.text.secondary} text-sm`}>
                    Vaccine Name
                  </label>
                  <p className={`${themeClasses.text.primary} font-medium`}>
                    {vaccination.vaccineName}
                  </p>
                </div>

                <div>
                  <label className={`${themeClasses.text.secondary} text-sm`}>
                    Manufacturer
                  </label>
                  <p className={`${themeClasses.text.primary} font-medium`}>
                    {vaccination.manufacturer}
                  </p>
                </div>

                <div>
                  <label className={`${themeClasses.text.secondary} text-sm`}>
                    Batch Number
                  </label>
                  <p className={`${themeClasses.text.primary} font-medium`}>
                    {vaccination.batchNumber}
                  </p>
                </div>

                <div>
                  <label className={`${themeClasses.text.secondary} text-sm`}>
                    Date Administered
                  </label>
                  <p
                    className={`${themeClasses.text.primary} font-medium flex items-center`}
                  >
                    <Calendar
                      className={`w-4 h-4 mr-1 ${themeClasses.text.tertiary}`}
                    />
                    {formatDate(vaccination.dateAdministered)}
                  </p>
                </div>

                <div>
                  <label className={`${themeClasses.text.secondary} text-sm`}>
                    Valid Until
                  </label>
                  <p
                    className={`${themeClasses.text.primary} font-medium flex items-center`}
                  >
                    <Clock
                      className={`w-4 h-4 mr-1 ${themeClasses.text.tertiary}`}
                    />
                    {formatDate(vaccination.validUntil)}
                  </p>
                </div>
              </div>

              {/* Veterinarian Details */}
              <div className="space-y-4">
                <h3
                  className={`text-lg font-semibold ${themeClasses.text.primary} mb-3`}
                >
                  Veterinarian Information
                </h3>

                <div>
                  <label className={`${themeClasses.text.secondary} text-sm`}>
                    Veterinarian
                  </label>
                  <p
                    className={`${themeClasses.text.primary} font-medium flex items-center`}
                  >
                    <User
                      className={`w-4 h-4 mr-1 ${themeClasses.text.tertiary}`}
                    />
                    {vaccination.vetName}
                  </p>
                </div>

                <div>
                  <label className={`${themeClasses.text.secondary} text-sm`}>
                    Contact
                  </label>
                  <p
                    className={`${themeClasses.text.primary} font-medium flex items-center`}
                  >
                    <Phone
                      className={`w-4 h-4 mr-1 ${themeClasses.text.tertiary}`}
                    />
                    {vaccination.vetContact}
                  </p>
                </div>

                <div>
                  <label className={`${themeClasses.text.secondary} text-sm`}>
                    License Number
                  </label>
                  <p className={`${themeClasses.text.primary} font-medium`}>
                    {vaccination.vetLicense}
                  </p>
                </div>

                <div>
                  <label className={`${themeClasses.text.secondary} text-sm`}>
                    Clinic Address
                  </label>
                  <p
                    className={`${themeClasses.text.primary} font-medium flex items-center`}
                  >
                    <MapPin
                      className={`w-4 h-4 mr-1 ${themeClasses.text.tertiary}`}
                    />
                    {vaccination.clinicAddress}
                  </p>
                </div>
              </div>
            </div>

            {/* Notes */}
            {vaccination.notes && (
              <div
                className={`mt-6 pt-6 border-t ${themeClasses.border.primary}`}
              >
                <h3
                  className={`text-lg font-semibold ${themeClasses.text.primary} mb-3`}
                >
                  Notes
                </h3>
                <p
                  className={`${themeClasses.text.secondary} ${themeClasses.bg.secondary} rounded-lg p-3`}
                >
                  {vaccination.notes}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1
              className={`text-2xl font-bold ${themeClasses.text.primary} flex items-center`}
            >
              <Shield className="w-6 h-6 mr-2 text-cyan-400" />
              Vaccination Records
            </h1>
            <p className={`${themeClasses.text.secondary} mt-1`}>
              Manage and view vaccination records for your pets
            </p>
          </div>
        </div>

        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-12`}
        >
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mb-4"></div>
            <h3
              className={`text-lg font-semibold ${themeClasses.text.primary} mb-2`}
            >
              Loading Your Pets
            </h3>
            <p className={themeClasses.text.secondary}>
              Please wait while we fetch your pet information...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={`text-2xl font-bold ${themeClasses.text.primary} flex items-center`}
          >
            <Shield className="w-6 h-6 mr-2 text-cyan-400" />
            Vaccination Records
          </h1>
          <p className={`${themeClasses.text.secondary} mt-1`}>
            Manage and view vaccination records for your pets
          </p>
        </div>
      </div>

      {pets.length === 0 ? (
        <div className="text-center py-12">
          <Shield
            className={`w-16 h-16 ${themeClasses.text.tertiary} mx-auto mb-4`}
          />
          <h3
            className={`text-xl font-semibold ${themeClasses.text.primary} mb-2`}
          >
            No Pets Found
          </h3>
          <p className={`${themeClasses.text.secondary} mb-4`}>
            You need to register pets first to manage vaccination records.
          </p>
          <button
            className={`${themeClasses.bg.button} text-white px-4 py-2 rounded-lg transition-colors`}
          >
            Register Pet
          </button>
        </div>
      ) : (
        <>
          {/* Pet Selection */}
          <div
            className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
          >
            <h3
              className={`${themeClasses.text.primary} font-semibold mb-4 flex items-center`}
            >
              <User className="w-5 h-5 mr-2 text-cyan-400" />
              Select Pet
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pets.map((pet) => (
                <button
                  key={pet.id}
                  onClick={() => setSelectedPet(pet)}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                    selectedPet?.id === pet.id
                      ? "border-cyan-500 bg-cyan-500/10 shadow-lg shadow-cyan-500/20"
                      : `${themeClasses.border.primary} ${themeClasses.bg.secondary} hover:border-cyan-400 hover:shadow-md`
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {/* Pet Avatar */}
                    <div className="relative">
                      {(() => {
                        // PRIORITY 1: Use instant-loading thumbnail for avatar
                        if (pet.avatarThumbnail) {
                          return (
                            <img
                              src={pet.avatarThumbnail}
                              alt={pet.name}
                              className="w-12 h-12 rounded-full object-cover border-2 border-gray-300"
                              style={{ imageRendering: "auto" }}
                            />
                          );
                        }

                        // PRIORITY 2: Handle new format with photos object (frontFace, leftSide, rightSide, etc.)
                        if (
                          pet.photos &&
                          typeof pet.photos === "object" &&
                          !Array.isArray(pet.photos)
                        ) {
                          const frontFacePhoto = pet.photos.frontFace;
                          if (frontFacePhoto && frontFacePhoto.url) {
                            return (
                              <img
                                src={frontFacePhoto.url}
                                alt={pet.name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-300"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            );
                          }
                          // Try other photo types if frontFace is not available
                          const photoTypes = [
                            "leftSide",
                            "rightSide",
                            "fullBody",
                          ];
                          for (const type of photoTypes) {
                            if (pet.photos[type] && pet.photos[type].url) {
                              return (
                                <img
                                  src={pet.photos[type].url}
                                  alt={pet.name}
                                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-300"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                              );
                            }
                          }
                        }

                        // Handle old format with photos array
                        if (
                          pet.photos &&
                          Array.isArray(pet.photos) &&
                          pet.photos.length > 0
                        ) {
                          const photo = pet.photos[0];
                          let imageSrc;

                          if (photo.startsWith("data:")) {
                            // Already a data URL
                            imageSrc = photo;
                          } else if (photo.startsWith("http")) {
                            // Firebase Storage URL or other HTTP URL
                            imageSrc = photo;
                          } else {
                            // Assume it's base64 string
                            imageSrc = `data:image/jpeg;base64,${photo}`;
                          }

                          return (
                            <img
                              src={imageSrc}
                              alt={pet.name}
                              className="w-12 h-12 rounded-full object-cover border-2 border-gray-300"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                          );
                        }

                        // Handle imageUrl format
                        if (pet.imageUrl) {
                          return (
                            <img
                              src={pet.imageUrl}
                              alt={pet.name}
                              className="w-12 h-12 rounded-full object-cover border-2 border-gray-300"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                          );
                        }

                        return null;
                      })()}
                      {/* Default Avatar Fallback */}
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                          pet.species === "dog"
                            ? "bg-amber-500"
                            : pet.species === "cat"
                            ? "bg-purple-500"
                            : "bg-gray-500"
                        } ${
                          (pet.photos && pet.photos.length > 0) || pet.imageUrl
                            ? "hidden"
                            : "flex"
                        }`}
                        style={{
                          display:
                            (pet.photos && pet.photos.length > 0) ||
                            pet.imageUrl
                              ? "none"
                              : "flex",
                        }}
                      >
                        {pet.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      {/* Selection Indicator */}
                      {selectedPet?.id === pet.id && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-cyan-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Pet Info */}
                    <div className="flex-1 min-w-0">
                      <h4
                        className={`font-semibold ${
                          selectedPet?.id === pet.id
                            ? "text-cyan-600"
                            : themeClasses.text.primary
                        } truncate`}
                      >
                        {pet.name}
                      </h4>
                      <p
                        className={`text-sm ${themeClasses.text.secondary} capitalize`}
                      >
                        {pet.species} • {pet.breed || "Mixed"}
                      </p>
                      {pet.age && (
                        <p className={`text-xs ${themeClasses.text.tertiary}`}>
                          {pet.age} years old
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {selectedPet && (
            <>
              {/* Filters and Search */}
              <div
                className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-4`}
              >
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <Search
                        className={`w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.text.tertiary}`}
                      />
                      <input
                        type="text"
                        placeholder="Search vaccinations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg pl-10 pr-4 py-2 ${themeClasses.text.primary} placeholder-gray-400 focus:border-cyan-500 focus:outline-none`}
                      />
                    </div>

                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className={`${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg px-3 py-2 ${themeClasses.text.primary} focus:border-cyan-500 focus:outline-none`}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="expiring_soon">Expiring Soon</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>

                  <div className={`text-sm ${themeClasses.text.secondary}`}>
                    {filteredVaccinations.length} vaccination
                    {filteredVaccinations.length !== 1 ? "s" : ""} found
                  </div>
                </div>
              </div>

              {/* Vaccination Records */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3
                    className={`text-lg font-semibold ${themeClasses.text.primary}`}
                  >
                    Vaccinations for {selectedPet.name}
                  </h3>
                  <button
                    onClick={addTestVaccination}
                    className={`flex items-center space-x-2 ${themeClasses.bg.button} text-white px-3 py-2 rounded-lg transition-colors text-sm`}
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Test Vaccination</span>
                  </button>
                </div>

                {loadingVaccinations ? (
                  <div
                    className={`text-center py-8 ${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary}`}
                  >
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mr-3"></div>
                      <span className={themeClasses.text.secondary}>
                        Loading vaccination records...
                      </span>
                    </div>
                  </div>
                ) : filteredVaccinations.length === 0 ? (
                  <div
                    className={`text-center py-8 ${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary}`}
                  >
                    <Shield
                      className={`w-12 h-12 ${themeClasses.text.tertiary} mx-auto mb-3`}
                    />
                    <h4
                      className={`text-lg font-semibold ${themeClasses.text.primary} mb-2`}
                    >
                      No Vaccination Records
                    </h4>
                    <p className={themeClasses.text.secondary}>
                      {searchTerm || filterStatus !== "all"
                        ? "No records match your search criteria."
                        : "No vaccination records found for this pet."}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredVaccinations.map((vaccination) => (
                      <VaccinationCard
                        key={vaccination.id}
                        vaccination={vaccination}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Vaccination Detail Modal */}
      {selectedVaccination && (
        <VaccinationDetailModal
          vaccination={selectedVaccination}
          onClose={() => setSelectedVaccination(null)}
        />
      )}
    </div>
  );
};

export default VaccinationRecordsPage;
